import React, { useState, useMemo, useEffect } from 'react';
import { usePageTitle } from '../hooks/usePageTitle';

function useIsMobile(): boolean {
  const [mobile, setMobile] = useState(false);
  useEffect(() => {
    const m = window.matchMedia('(max-width: 639px)');
    const update = () => setMobile(m.matches);
    update();
    m.addEventListener('change', update);
    return () => m.removeEventListener('change', update);
  }, []);
  return mobile;
}

const FEET_TO_METERS = 0.3048;
const WINGO_METERS = 320;

function feetInchesToMeters(feet: number, inches: number): number {
  const totalFeet = feet + inches / 12;
  return totalFeet * FEET_TO_METERS;
}

function metersToWingo(meters: number): number {
  return meters / WINGO_METERS;
}

function metersToFeetInches(meters: number): { ft: number; in: number } {
  const totalFeet = meters / FEET_TO_METERS;
  const ft = Math.floor(totalFeet);
  const inch = (totalFeet - ft) * 12;
  return { ft, in: inch };
}

function metersToFeetInchesRounded(meters: number): { ft: number; inch: number } {
  const totalFeet = meters / FEET_TO_METERS;
  const ft = Math.floor(totalFeet);
  let inch = Math.round((totalFeet - ft) * 12);
  // Carry overflow (e.g. 12 inches -> next foot)
  if (inch === 12) return { ft: ft + 1, inch: 0 };
  return { ft, inch };
}

/** Daisy's Distances: label + meters (aligned with wingo320.com/converter). */
const DAISY_FAVORITES: Array<{ label: string; meters: number }> = [
  { label: 'Wingito', meters: 160 },
  { label: 'Wingo', meters: 320 },
  { label: 'Twingo', meters: 640 },
  { label: '2.5 Wingo', meters: 800 },
  { label: 'Tringo', meters: 960 },
  { label: 'PentaWingo', meters: 1600 },
  { label: 'DecaWingo', meters: 3200 },
  { label: 'Madringo', meters: 15000 },
  { label: 'Half Mare', meters: 21097.5 },
  { label: 'Sevillingo', meters: 42000 },
  { label: 'Mare-athon', meters: 42195 },
  { label: 'Yellowstone', meters: 644000 },
];

type InputMode = 'ftin' | 'meters' | 'wingo';

const WingoImperialingo: React.FC = () => {
  usePageTitle('320 Day (3/19) | wingo320.com');
  const isMobile = useIsMobile();
  const [inputMode, setInputMode] = useState<InputMode>('meters');
  const [feet, setFeet] = useState<string>('');
  const [inches, setInches] = useState<string>('');
  const [metersInput, setMetersInput] = useState<string>('320');
  const [wingoInput, setWingoInput] = useState<string>('');

  const parsedFeet = useMemo(() => {
    const n = parseInt(feet, 10);
    return Number.isNaN(n) || n < 0 ? null : n;
  }, [feet]);

  const parsedInches = useMemo(() => {
    if (inches === '') return 0;
    const n = parseFloat(inches);
    if (Number.isNaN(n) || n < 0) return null;
    if (n > 11) return null;
    return n;
  }, [inches]);

  const parsedMeters = useMemo(() => {
    const n = parseFloat(metersInput);
    return (metersInput !== '' && !Number.isNaN(n) && n >= 0) ? n : null;
  }, [metersInput]);

  const parsedWingo = useMemo(() => {
    const n = parseFloat(wingoInput);
    return (wingoInput !== '' && !Number.isNaN(n) && n >= 0) ? n : null;
  }, [wingoInput]);

  const { meters, wingo, feetInches } = useMemo(() => {
    if (inputMode === 'ftin') {
      if (parsedFeet === null || parsedInches === null) {
        return { meters: null, wingo: null, feetInches: null };
      }
      const inchesVal = typeof parsedInches === 'number' ? parsedInches : 0;
      const m = feetInchesToMeters(parsedFeet, inchesVal);
      const w = metersToWingo(m);
      return { meters: m, wingo: w, feetInches: metersToFeetInches(m) };
    }
    if (inputMode === 'meters' && parsedMeters !== null) {
      const m = parsedMeters;
      const w = metersToWingo(m);
      return { meters: m, wingo: w, feetInches: metersToFeetInches(m) };
    }
    if (inputMode === 'wingo' && parsedWingo !== null) {
      const m = parsedWingo * WINGO_METERS;
      const w = parsedWingo;
      return { meters: m, wingo: w, feetInches: metersToFeetInches(m) };
    }
    return { meters: null, wingo: null, feetInches: null };
  }, [inputMode, parsedFeet, parsedInches, parsedMeters, parsedWingo]);

  const handleFeetChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const v = e.target.value;
    if (v === '' || /^\d+$/.test(v)) setFeet(v);
  };

  const handleInchesChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const v = e.target.value;
    if (v === '') { setInches(v); return; }
    if (!/^\d*\.?\d*$/.test(v)) return;
    const n = parseFloat(v);
    if (!Number.isNaN(n) && n > 11) return;
    setInches(v);
  };

  const handleMetersChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const v = e.target.value;
    if (v === '' || /^\d*\.?\d*$/.test(v)) setMetersInput(v);
  };

  const handleWingoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const v = e.target.value;
    if (v === '' || /^\d*\.?\d*$/.test(v)) setWingoInput(v);
  };

  const setPreset = (presetFeet: number, presetInches: number = 0) => {
    setInputMode('ftin');
    setFeet(String(presetFeet));
    setInches(String(presetInches));
  };

  const setPresetMeters = (presetMeters: number) => {
    setInputMode('meters');
    setMetersInput(String(presetMeters));
  };

  const setPresetWingos = (presetWingo: number) => {
    setInputMode('wingo');
    setWingoInput(String(presetWingo));
  };

  const dash = '—';
  const metersStr = meters != null ? meters.toFixed(1) : dash;
  const wingoStr3 = wingo != null ? wingo.toFixed(3) : dash;
  const imperialStr =
    feetInches != null
      ? `${feetInches.ft}′ ${Math.round(feetInches.in)}″`
      : dash;

  const inputDescription =
    inputMode === 'ftin'
      ? (feet !== '' || inches !== '' ? `${feet || '0'} ft ${inches || '0'} in` : null)
      : inputMode === 'meters'
        ? (metersInput !== '' ? `${metersInput}m` : null)
        : (wingoInput !== '' ? `${wingoInput} Wingos` : null);
  const inputDescMobile =
    inputMode === 'ftin' && (feet !== '' || inches !== '')
      ? `${feet || '0'}′ ${inches || '0'}″`
      : inputMode === 'meters' && metersInput !== ''
        ? `${metersInput}m`
        : wingoInput !== ''
          ? `${wingoInput} W`
          : null;
  const wingoLabel = isMobile ? 'W' : 'Wingos';
  const formatImperial = (ft: number, inch: number) =>
    isMobile ? `${ft}′ ${Math.round(inch)}″` : `${ft.toLocaleString()} ft ${Math.round(inch)} in`;
  const mUnit = isMobile ? 'm' : ' m';
  const conversionLine =
    inputDescription != null && meters != null && wingo != null && feetInches != null
      ? isMobile
        ? inputMode === 'ftin'
          ? `${inputDescMobile} = ${wingoStr3} W = ${metersStr}${mUnit}`
          : inputMode === 'meters'
            ? `${inputDescMobile} = ${wingoStr3} W = ${formatImperial(feetInches.ft, feetInches.in)}`
            : `${inputDescMobile} = ${metersStr}${mUnit} = ${formatImperial(feetInches.ft, feetInches.in)}`
        : inputMode === 'ftin'
          ? `${inputDescription} = ${wingoStr3} ${wingoLabel} = ${metersStr} m`
          : inputMode === 'meters'
            ? `${inputDescription} = ${wingoStr3} ${wingoLabel} = ${formatImperial(feetInches.ft, feetInches.in)}`
            : `${inputDescription} = ${metersStr} m = ${formatImperial(feetInches.ft, feetInches.in)}`
      : null;

  const [conversionFlash, setConversionFlash] = useState(false);
  const prevResultRef = React.useRef<string>('');
  useEffect(() => {
    const key = conversionLine ?? '';
    if (prevResultRef.current !== '' && prevResultRef.current !== key) {
      setConversionFlash(true);
      const t = setTimeout(() => setConversionFlash(false), 500);
      return () => clearTimeout(t);
    }
    prevResultRef.current = key;
  }, [conversionLine]);

  const randomIntInclusive = (min: number, max: number): number =>
    Math.floor(min + Math.random() * (max - min + 1));

  const handleRandomize = () => {
    const MIN_METERS = 10;
    const MAX_METERS = 320;

    // Always generate in meters; then derive ft/in + wingos.
    const metersCandidate = randomIntInclusive(MIN_METERS, MAX_METERS);
    const { ft, inch } = metersToFeetInchesRounded(metersCandidate);
    const metersRoundedFromFtIn = feetInchesToMeters(ft, inch);
    const wingosRounded3 = Math.round((metersRoundedFromFtIn / WINGO_METERS) * 1000) / 1000;

    // Also drive the converter inputs from whatever mode the user is currently using.
    // This keeps the random button "next to shortcuts" feeling functional.
    if (inputMode === 'meters') {
      setMetersInput(String(Math.round(metersRoundedFromFtIn)));
    } else if (inputMode === 'ftin') {
      setFeet(String(ft));
      setInches(String(inch));
    } else if (inputMode === 'wingo') {
      setWingoInput(wingosRounded3.toFixed(3));
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-8">
        <header className="text-center mb-6 sm:mb-8">
          <h1 className="text-2xl sm:text-4xl font-bold text-gray-900">
            320 Day (3/19)
          </h1>
          <p className="hidden sm:block text-base sm:text-xl text-gray-600 mt-0.5">
            Counting every meter. Because every meter counts.
          </p>
        </header>

        <div className="max-w-lg sm:max-w-xl mx-auto">
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
            {/* Distance: where user types → flow into Conversion */}
            <div className="p-4 sm:p-6 border-b border-gray-100 relative ring-inset ring-1 ring-gray-200/60 rounded-t-xl bg-white">
              <div className="flex justify-between items-center gap-3 mb-3 sm:mb-4">
                <h2 className="text-base sm:text-lg font-semibold text-gray-800">
                  Wingo Imperialingo
                </h2>
                <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-900 text-white shrink-0">
                  D<span className="text-[#00bcd4] font-semibold">AI</span>SY™
                </span>
              </div>
              <div className="flex flex-wrap gap-2 mb-3">
                <button
                  type="button"
                  onClick={() => setInputMode('meters')}
                  className={`min-h-[40px] px-3 py-2 text-sm font-medium rounded-md ${inputMode === 'meters' ? 'bg-gray-900 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
                >
                  Meters
                </button>
                <button
                  type="button"
                  onClick={() => setInputMode('ftin')}
                  className={`min-h-[40px] px-3 py-2 text-sm font-medium rounded-md ${inputMode === 'ftin' ? 'bg-gray-900 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
                >
                  ft / in
                </button>
                <button
                  type="button"
                  onClick={() => setInputMode('wingo')}
                  className={`min-h-[40px] px-3 py-2 text-sm font-medium rounded-md ${inputMode === 'wingo' ? 'bg-gray-900 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
                >
                  Wingos
                </button>
              </div>

              {inputMode === 'ftin' && (
                <div className="flex flex-wrap items-center gap-2">
                  <input
                    type="text"
                    inputMode="numeric"
                    value={feet}
                    onChange={handleFeetChange}
                    placeholder="1000"
                    className="min-h-[44px] w-20 sm:w-24 px-3 py-2.5 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-wingo-500 focus:border-wingo-500 text-gray-900 text-base placeholder:text-gray-400"
                    aria-label="Feet"
                  />
                  <span className="text-gray-600 font-medium">ft</span>
                  <input
                    type="text"
                    inputMode="decimal"
                    value={inches}
                    onChange={handleInchesChange}
                    placeholder="0"
                    className="min-h-[44px] w-20 sm:w-24 px-3 py-2.5 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-wingo-500 focus:border-wingo-500 text-gray-900 text-base placeholder:text-gray-400"
                    aria-label="Inches"
                  />
                  <span className="text-gray-600 font-medium">in</span>
                </div>
              )}
              {inputMode === 'meters' && (
                <div className="flex items-center gap-2">
                  <input
                    type="text"
                    inputMode="decimal"
                    value={metersInput}
                    onChange={handleMetersChange}
                    placeholder="320"
                    className="min-h-[44px] w-28 px-3 py-2.5 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-wingo-500 focus:border-wingo-500 text-gray-900 text-base"
                    aria-label="Meters"
                  />
                  <span className="text-gray-600 font-medium">m</span>
                </div>
              )}
              {inputMode === 'wingo' && (
                <div className="flex items-center gap-2">
                  <input
                    type="text"
                    inputMode="decimal"
                    value={wingoInput}
                    onChange={handleWingoChange}
                    placeholder="5"
                    className="min-h-[44px] w-24 px-3 py-2.5 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-wingo-500 focus:border-wingo-500 text-gray-900 text-base"
                    aria-label="Wingos"
                  />
                  <span className="text-gray-600 font-medium">Wingos</span>
                </div>
              )}

              <div className="mt-4 pt-3 border-t border-gray-100">
                <p className="text-[11px] text-gray-500 uppercase tracking-wider mb-2">Shortcuts</p>
                <div className="flex flex-wrap gap-2">
                  <button type="button" onClick={() => setPresetWingos(1)} className="min-h-[36px] px-2.5 py-1.5 text-xs border border-gray-300 rounded-md text-gray-700 bg-white hover:bg-gray-50">1 Wingo</button>
                  <button type="button" onClick={() => setPresetMeters(800)} className="min-h-[36px] px-2.5 py-1.5 text-xs border border-gray-300 rounded-md text-gray-700 bg-white hover:bg-gray-50">800m</button>
                  <button type="button" onClick={() => setPresetWingos(3)} className="min-h-[36px] px-2.5 py-1.5 text-xs border border-gray-300 rounded-md text-gray-700 bg-white hover:bg-gray-50 sm:hidden">Tringo</button>
                  <button type="button" onClick={() => setPresetWingos(5)} className="hidden sm:inline-flex items-center min-h-[36px] px-2.5 py-1.5 text-xs border border-gray-300 rounded-md text-gray-700 bg-white hover:bg-gray-50">PentaWingo</button>
                  <button type="button" onClick={() => setPreset(1000, 0)} className="hidden sm:inline-flex items-center justify-center min-h-[36px] px-2.5 py-1.5 text-xs border border-gray-300 rounded-md text-gray-700 bg-white hover:bg-gray-50">1,000 ft</button>
                  <button
                    type="button"
                    onClick={handleRandomize}
                    className="min-h-[36px] px-3 py-1.5 text-xs border border-wingo-600 rounded-md text-wingo-50 bg-wingo-600 hover:bg-wingo-700 flex items-center justify-center"
                    aria-label="Random"
                  >
                    Random
                  </button>
                </div>
              </div>
            </div>

            {/* Arrow: Distance → Conversion (show flow) */}
            <div className="flex justify-center items-center py-2 bg-gray-100 border-y border-gray-200" aria-hidden="true">
              <span className="text-gray-500 text-xl leading-none" style={{ lineHeight: 1 }}>↓</span>
            </div>

            {/* Conversion: result of what you entered above */}
            <div className={`p-4 sm:p-6 border-b border-gray-100 transition-colors duration-300 ${conversionFlash ? 'bg-amber-50/70' : 'bg-gray-50'} border-l-4 border-l-wingo-500/50`}>
              {conversionLine != null ? (
                <>
                  <p className="text-xs text-gray-500 mb-1.5">Converting: {inputDescription}</p>
                  <p className={`font-mono text-base sm:text-lg font-semibold leading-snug break-words py-2.5 px-3 rounded-lg bg-white/80 border border-gray-200/80 transition-colors duration-200 ${conversionFlash ? 'text-wingo-800 border-wingo-200' : 'text-gray-900'}`}>
                    {conversionLine}
                  </p>
                </>
              ) : (
                <p className="text-sm text-gray-500 py-1">Enter distance above to see conversion.</p>
              )}
            </div>

            <div className="px-4 sm:px-6 py-3 sm:py-4 bg-gray-50">
              <p className="text-xs text-gray-500 text-center">
                Measurements subject to DAISY verification.
              </p>
            </div>
          </div>

          <section className="mt-6 sm:mt-8" aria-labelledby="daisy-distances-heading">
            <h2 id="daisy-distances-heading" className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-3 sm:mb-4 text-center sm:text-left">
              Daisy&apos;s Distances
            </h2>
            <div className="overflow-x-auto -mx-1 sm:mx-0 rounded-xl border border-gray-200 bg-white shadow-sm">
              <table className="w-full min-w-[280px] border-collapse text-left">
                <caption className="sr-only">
                  Daisy&apos;s distances in meters, wingos, and feet.
                </caption>
                <thead>
                  <tr className="bg-gray-50 border-b border-gray-200">
                    <th scope="col" className="px-3 sm:px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">Distance</th>
                    <th scope="col" className="px-3 sm:px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider text-right whitespace-nowrap">Wingos</th>
                    <th scope="col" className="px-3 sm:px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider text-right whitespace-nowrap">Meters</th>
                    <th scope="col" className="px-3 sm:px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider text-right whitespace-nowrap min-w-[4.5rem]">Feet</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {DAISY_FAVORITES.map((row) => {
                    const m = row.meters;
                    const w = m / WINGO_METERS;
                    const metersDisplay = row.label === 'Half Mare'
                      ? `${(Math.round(m * 10) / 10).toLocaleString()}m`
                      : `${Math.round(m).toLocaleString()}m`;
                    const totalFeet = m / FEET_TO_METERS;
                    const feetFormatted = Number(totalFeet.toFixed(1)).toLocaleString(undefined, { minimumFractionDigits: 1, maximumFractionDigits: 1 });
                    const feetStr = `${feetFormatted} ft`;
                    const wStep = Math.round(w * 2) / 2; // Wingos are displayed in 0.5 W steps (Wingito=0.5)
                    const wStr = Number.isInteger(wStep) ? `${wStep}` : `${wStep.toFixed(1)}`;
                    return (
                      <tr key={row.label} className="hover:bg-gray-50/50">
                        <td className="px-3 sm:px-4 py-2.5 sm:py-3 text-sm text-gray-900 whitespace-nowrap">{row.label}</td>
                        <td className="px-3 sm:px-4 py-2.5 sm:py-3 text-sm font-mono text-gray-800 text-right tabular-nums whitespace-nowrap">{wStr} W</td>
                        <td className="px-3 sm:px-4 py-2.5 sm:py-3 text-sm font-mono text-gray-800 text-right tabular-nums whitespace-nowrap">{metersDisplay}</td>
                        <td className="px-3 sm:px-4 py-2.5 sm:py-3 text-sm font-mono text-gray-800 text-right tabular-nums whitespace-nowrap min-w-[4.5rem]">{feetStr}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
};

export default WingoImperialingo;
